import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { FieldScheduleEntry, getWeekStart, formatTime } from '../../types/schedule';
import { getRefereeWeekSchedule } from '../../services/scheduleService';

const Container = styled.div`
  background: var(--legacy-card-bg, #0d1b2a);
  border: 1px solid #00d4ff;
  border-radius: 12px;
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid rgba(0, 212, 255, 0.2);
`;

const Title = styled.h3`
  margin: 0;
  font-size: 16px;
  color: #00d4ff;
  font-family: 'Orbitron', sans-serif;
`;

const NavButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const NavButton = styled.button`
  background: rgba(0, 212, 255, 0.1);
  border: 1px solid rgba(0, 212, 255, 0.3);
  color: #00d4ff;
  padding: 6px 12px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(0, 212, 255, 0.2);
  }
`;

const Content = styled.div`
  padding: 16px;
`;

const ShiftsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ShiftCard = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 16px;
  background: rgba(0, 212, 255, 0.1);
  border: 1px solid rgba(0, 212, 255, 0.2);
  border-radius: 8px;
`;

const DateBadge = styled.div`
  text-align: center;
  min-width: 50px;
`;

const DayName = styled.div`
  font-size: 10px;
  color: #8bb8d8;
  text-transform: uppercase;
`;

const DayNumber = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: #00d4ff;
  font-family: 'Orbitron', sans-serif;
`;

const MonthName = styled.div`
  font-size: 10px;
  color: #8bb8d8;
`;

const ShiftDetails = styled.div`
  flex: 1;
`;

const ShiftTime = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #fff;
  font-family: 'Orbitron', monospace;
`;

const ShiftMeta = styled.div`
  font-size: 12px;
  color: #8bb8d8;
  margin-top: 4px;
`;

const GameTypeBadge = styled.span`
  font-size: 10px;
  padding: 2px 8px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  font-weight: 500;
  text-transform: capitalize;
  margin-left: 8px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 30px 20px;
  color: #8bb8d8;
  font-size: 14px;
`;

interface RefereeScheduleSectionProps {
  refereeId: string;
}

const RefereeScheduleSection: React.FC<RefereeScheduleSectionProps> = ({ refereeId }) => {
  const [weekStart, setWeekStart] = useState<Date>(() => getWeekStart(new Date()));
  const [shifts, setShifts] = useState<FieldScheduleEntry[]>([]);
  const [loading, setLoading] = useState(true);

  const loadShifts = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getRefereeWeekSchedule(refereeId, weekStart);
      // Sort by date and time
      data.sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
      setShifts(data);
    } catch (error) {
      console.error('Error loading referee schedule:', error);
      setShifts([]);
    } finally {
      setLoading(false);
    }
  }, [refereeId, weekStart]);

  useEffect(() => {
    loadShifts();
  }, [loadShifts]);

  const goToPreviousWeek = () => {
    const newDate = new Date(weekStart);
    newDate.setDate(newDate.getDate() - 7);
    setWeekStart(newDate);
  };

  const goToNextWeek = () => {
    const newDate = new Date(weekStart);
    newDate.setDate(newDate.getDate() + 7);
    setWeekStart(newDate);
  };

  const formatWeekRange = () => {
    const endDate = new Date(weekStart);
    endDate.setDate(endDate.getDate() + 6);
    const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
    return `${weekStart.toLocaleDateString('en-US', options)} - ${endDate.toLocaleDateString('en-US', options)}`;
  };

  const getDayInfo = (dateStr: string) => {
    // Парсим дату без конвертации таймзоны
    const datePart = dateStr.split('T')[0];
    const [year, month, day] = datePart.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return {
      dayName: dayNames[date.getDay()],
      dayNumber: date.getDate(),
      monthName: monthNames[date.getMonth()]
    };
  };

  return (
    <Container>
      <Header>
        <Title>My Schedule - {formatWeekRange()}</Title>
        <NavButtons>
          <NavButton onClick={goToPreviousWeek}>&larr;</NavButton>
          <NavButton onClick={goToNextWeek}>&rarr;</NavButton>
        </NavButtons>
      </Header>

      <Content>
        {loading ? (
          <EmptyState>Loading...</EmptyState>
        ) : shifts.length === 0 ? (
          <EmptyState>No shifts scheduled for this week</EmptyState>
        ) : (
          <ShiftsList>
            {shifts.map(shift => {
              const dayInfo = getDayInfo(shift.date);
              return (
                <ShiftCard key={shift.id}>
                  <DateBadge>
                    <DayName>{dayInfo.dayName}</DayName>
                    <DayNumber>{dayInfo.dayNumber}</DayNumber>
                    <MonthName>{dayInfo.monthName}</MonthName>
                  </DateBadge>
                  <ShiftDetails>
                    <ShiftTime>
                      {formatTime(shift.startTime)} - {formatTime(shift.endTime)}
                    </ShiftTime>
                    <ShiftMeta>
                      Shift
                      {shift.gameType && (
                        <GameTypeBadge>{shift.gameType}</GameTypeBadge>
                      )}
                    </ShiftMeta>
                  </ShiftDetails>
                </ShiftCard>
              );
            })}
          </ShiftsList>
        )}
      </Content>
    </Container>
  );
};

export default RefereeScheduleSection;
